/* eslint-disable @nx/enforce-module-boundaries */
import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Link,
  LinkButton,
  SeeMore,
  Stack,
  Text,
  Tooltip,
  IconButton,
} from '@mybridge/ui';
import moment from 'moment';

import React, { forwardRef, useContext, useEffect, useState, useRef } from 'react';
import { MyProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { formatCommaSeparated } from 'v4/lib/commons';
import {
  AddCircle,
  EarthGlobeIcon,
  TeamIcon,
  OnlyMeLockIcon,
  AddCircleFIlled,
  NewPlusIcon
} from '@mybridge/icons';
import { EditIcon } from '@mybridge/icons/Edit';
import { isMobile } from 'react-device-detect';
import { MyProfileCertificateModal } from '../certificates-modal';
import { AddIcon } from '@chakra-ui/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useRouter } from 'next/router';

// Wrap the entire MyProfileCertificate component with React.memo
export const MyProfileCertificate = React.memo(forwardRef(({ ...props }, ref) => {
  const {
    userProfileInfo,
    certificatesDisc,
    myCertificates,
    refetchMyCertificates,
    setSelectedCertificate,
    entityHasStartDates,
    entityHasEndDates,
    audienceDisc,
    setPermissionValue,
    setPermissionId,
    setCurrentPermissionValue
  } = useContext(MyProfileContext);
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslator();
  const router = useRouter();
  const sectionRef = useRef(null);

  useEffect(() => {
    refetchMyCertificates?.();
  }, []);

  useEffect(() => {
    if (router.query.openDisc === 'certificates') {
      setSelectedCertificate(null);
      certificatesDisc?.onOpen?.();
      
      router.replace(router.pathname, undefined, { shallow: true });
    }
    
    if (router.query.scrollTo === 'certificates' && sectionRef.current) {
      // Simply scroll the element to the top of the viewport
      sectionRef.current.scrollIntoView({ 
        behavior: 'smooth',
        // block: 'start' // This positions the element at the top of the viewport
      });
      
      const { scrollTo, ...rest } = router.query;
      router.replace(
        { pathname: router.pathname, query: rest },
        undefined,
        { shallow: true }
      );
    }
  }, [router.query, certificatesDisc, setSelectedCertificate]);

  const onEditClick = (ms) => (e) => {
    setSelectedCertificate(ms);
    certificatesDisc?.onOpen?.();
  };

  const renderPermissionSwitch = (param) => {
    switch (param) {
      case 1:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
      case 2:
        return {
          icon: <TeamIcon width="16px" height="16px" />,
          text: t('profileDetails.myNetwork') || 'myNetwork',
        };
      case 3:
        return {
          icon: <OnlyMeLockIcon width="16px" height="16px" />,
          text: t('profileDetails.onlyMe') || 'Only me',
        };
      default:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
    }
  };

  return (
    <>
      {certificatesDisc?.isOpen ? (
        <MyProfileCertificateModal />
      ) : (
        <Stack ref={sectionRef}>
          <HStack alignItems="flex-start">
            <Stack flex={1}>
              <Heading
                fontSize="18px"
                color="brandPrimary.500"
                fontWeight="400"
              >
                {t('profileCertificates.certificationsAndAwards') || "Certifications & Awards"}
              </Heading>
            </Stack>
            <HStack justifyContent="flex-end">
              {myCertificates?.length && (
                <IconButton
                  icon={<AddIcon color="#868585" />}
                  variant="transparent"
                  size={['sm', 'md']}
                  // color="brandPrimary.500"
                  onClick={(e) => certificatesDisc?.onOpen?.()}
                />
              )}
            </HStack>
          </HStack>
          {myCertificates?.length ? (
            <>
              <Stack pt={4} spacing={6}>
                {myCertificates
                  ?.filter?.((_, ind) => showAll || ind < 3)
                  ?.map?.((ms, msIndex) => (
                    <CertificateItem 
                      key={msIndex}
                      ms={ms}
                      msIndex={msIndex}
                      myCertificates={myCertificates}
                      onEditClick={onEditClick}
                      renderPermissionSwitch={renderPermissionSwitch}
                      audienceDisc={audienceDisc}
                      setPermissionValue={setPermissionValue}
                      setCurrentPermissionValue={setCurrentPermissionValue}
                      setPermissionId={setPermissionId}
                      entityHasStartDates={entityHasStartDates}
                      entityHasEndDates={entityHasEndDates}
                      t={t}
                    />
                  ))}
              </Stack>
              {!showAll && myCertificates?.length > 3 ? (
                <ShowAllLink onClick={(e) => setShowAll(true)} />
              ) : (
                ''
              )}
            </>
          ) : (
            <>
              <HStack justifyContent="flext-start">
             { isMobile ?
               (<> <Button
                  leftIcon={null} // Remove the default positioning of the icon
                  width="100%" // Adjust to desired width; fit-content will limit its flexibility
                  justifyContent="space-between" // Ensure icon moves to the end
                  variant="transparent"
                  minW={isMobile&&"93vw"}
                  color={isMobile ? '#747474' : 'brandPrimary.500'}
                  onClick={(e) => certificatesDisc?.onOpen?.()}
                  pl={0}
                >
                  <Box opacity={0.5}>{t('profileMilestones.addMilestone') || 'Add milestone'}</Box>
                  <NewPlusIcon width="20" height="20" />
                </Button>{' '}</>)
                :
                (<Button
                  leftIcon={<AddCircleFIlled />}
                  variant="transparent"
                  color="brandPrimary.500"
                  onClick={(e) => certificatesDisc?.onOpen?.()}
                  pl={0}
                >
                  <Box opacity={0.5}>

                  {t('profileCertificates.addCertificationsAndAwards') || "Add Certifications & Awards"}
                  </Box>
                </Button>)}
              </HStack>
            </>
          )}
        </Stack>
      )}
    </>
  );
}));

// Create a memoized CertificateItem component to prevent unnecessary re-renders
const CertificateItem = React.memo(({ 
  ms, 
  msIndex, 
  myCertificates, 
  onEditClick, 
  renderPermissionSwitch, 
  audienceDisc, 
  setPermissionValue, 
  setCurrentPermissionValue, 
  setPermissionId,
  entityHasStartDates,
  entityHasEndDates,
  t
}) => {
  return (
    <Stack key={msIndex}>
      <HStack key={msIndex} alignItems="flex-start">
        <Stack spacing={0} flex={1}>
          <Box>
            <Heading size="sm">{ms?.cert_name}</Heading>
          </Box>
          {entityHasStartDates(ms) || entityHasEndDates(ms) ? (
            <>
              <Box fontSize="sm">
                <Text display="inline">
                  {formatCommaSeparated(
                    [
                      moment()
                        .set(
                          'M',
                          parseInt((ms?.start_month === "0" ? ms?.start_month : ms?.start_month - 1) ?? '0')
                        )
                        .set('Y', ms?.start_year)
                        ?.format?.('MMMM YYYY'),
                      ms?.cert_not_expire
                        ? ''
                        : moment()
                          .set(
                            'M',
                            parseInt(ms?.end_month - 1 ?? '0')
                          )
                          .set('Y', ms?.end_year)
                          ?.format?.('MMMM YYYY'),
                    ],
                    ' - '
                  )}
                </Text>
              </Box>
            </>
          ) : (
            ''
          )}

          <Stack spacing={0}>
            {ms?.cert_authority ? (
              <Text>{ms?.cert_authority}</Text>
            ) : (
              ''
            )}
            {ms?.license_number ? (
              <Text>{ms?.license_number}</Text>
            ) : (
              ''
            )}
            {ms?.cert_url ? (
              <Link
                isExternal
                href={
                  ms?.cert_url.startsWith('http')
                    ? ms?.cert_url
                    : `https://${ms?.cert_url.includes('www.') ? ms?.cert_url : `www.${ms?.cert_url}`}`
                }
              >
                {ms?.cert_url.includes('www.') ? ms?.cert_url : `www.${ms?.cert_url}`}
              </Link>
            ) : (
              ''
            )}
          </Stack>
        </Stack>
        <Stack>
          <HStack>
            <Tooltip
              label={renderPermissionSwitch(ms?.audience)?.text}
            >
              <IconButton
                icon={
                  renderPermissionSwitch(ms?.audience)?.icon
                }
                size="sm"
                variant="transparent"
                onClick={() => {
                  audienceDisc.onOpen();
                  setPermissionValue('certs');
                  setCurrentPermissionValue(ms?.audience)
                  setPermissionId(ms?.id);
                }}
              />
            </Tooltip>
            <Tooltip label={t('buttons.edit') || 'Edit'}>
              <IconButton
                icon={<EditIcon width="16px" height="16px" />}
                size="sm"
                variant="transparent"
                onClick={onEditClick(ms)}
              />
            </Tooltip>
          </HStack>
        </Stack>
      </HStack>
      {myCertificates?.length > 1 &&
        msIndex < myCertificates?.length - 1 ? (
        <Divider />
      ) : (
        ''
      )}
    </Stack>
  );
});
